import React, { useState, useRef, useMemo, useCallback } from 'react';
import JoditEditor from 'jodit-react';
import styled from 'styled-components';

interface JoditEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  placeholder?: string;
}

const CustomJoditEditor: React.FC<JoditEditorProps> = ({
  content,
  onContentChange,
  placeholder = '내용을 입력해주세요...'
}) => {
  const editor = useRef(null);

  // debounced onChange 콜백 최적화
  const debouncedOnChange = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (newContent: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          onContentChange(newContent);
        }, 500); // 500ms 디바운스로 늘려서 성능 향상
      };
    })(),
    [onContentChange]
  );

  const config = useMemo(() => ({
    readonly: false,
    placeholder: placeholder,
    height: window.innerHeight - 200, // 헤더, 사이드바 등을 고려한 동적 높이
    language: 'ko',
    toolbarButtonSize: 'middle' as const,
    theme: 'default',
    
    // 한국어 폰트 지원
    defaultFontSizePoints: 'pt' as const,
    controls: {
      font: {
        list: {
          '맑은 고딕, Malgun Gothic, sans-serif': '맑은 고딕',
          '나눔고딕, NanumGothic, sans-serif': '나눔고딕',
          '나눔명조, NanumMyeongjo, serif': '나눔명조',
          '돋움, Dotum, sans-serif': '돋움',
          '굴림, Gulim, sans-serif': '굴림',
          '바탕, Batang, serif': '바탕',
          '궁서, Gungsuh, serif': '궁서',
          'Arial, sans-serif': 'Arial',
          'Times New Roman, serif': 'Times New Roman',
          'Courier New, monospace': 'Courier New',
          'Helvetica, sans-serif': 'Helvetica',
          'Georgia, serif': 'Georgia'
        }
      }
    },
    
    // 툴바 버튼 설정
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'superscript', 'subscript', '|',
      'ul', 'ol', '|',
      'outdent', 'indent', '|',
      'font', 'fontsize', 'brush', '|',
      'paragraph', 'lineHeight', '|',
      'image', 'link', 'table', '|',
      'left', 'center', 'right', 'justify', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'symbol', 'fullsize'
    ],
    
    // 이미지 업로드 설정
    uploader: {
      insertImageAsBase64URI: true,
      imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'svg', 'webp']
    },
    
    // 추가 설정
    removeButtons: ['file'],
    showXPathInStatusbar: false,
    showCharsCounter: false, // 성능 향상을 위해 비활성화
    showWordsCounter: false, // 성능 향상을 위해 비활성화
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    toolbarSticky: false,
    toolbarAdaptive: false,
    
    // 성능 최적화 설정 (이미지 기능은 유지)
    observer: {
      timeout: 200 // 변경 감지 간격 더 늘리기
    },
    useAceEditor: false, // Ace 에디터 비활성화
    beautifyHTML: false, // HTML 정리 기능 비활성화
    cleanHTML: {
      timeout: 300 // HTML 정리 타임아웃 늘리기
    },
    spellcheck: false, // 맞춤법 검사 비활성화
    enter: 'p' as const, // 엔터키 최적화
    processPasteHTML: false, // 붙여넣기 처리 단순화
    
    // 스타일 설정
    style: {
      background: '#fff',
      color: '#333'
    }
  }), [placeholder]);

  return (
    <EditorContainer>
      <JoditEditor
        ref={editor}
        value={content}
        config={config}
        tabIndex={1}
        onBlur={(newContent) => onContentChange(newContent)}
        onChange={debouncedOnChange}
      />
    </EditorContainer>
  );
};

const EditorContainer = styled.div`
  .jodit-container {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    
    &:focus-within {
      border-color: #667eea;
    }
  }
  
  .jodit-toolbar {
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px;
  }
  
  .jodit-toolbar-button {
    border-radius: 4px;
    margin: 2px;
    
    &:hover {
      background: #e5e7eb;
    }
    
    &.jodit-toolbar-button_active {
      background: #667eea;
      color: white;
    }
  }
  
  .jodit-workplace {
    background: white;
  }
  
  .jodit-wysiwyg {
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
    font-size: 14px;
    line-height: 1.6;
    padding: 16px;
    
    h1, h2, h3, h4, h5, h6 {
      font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
      margin: 16px 0 8px 0;
      font-weight: 600;
    }
    
    p {
      margin: 8px 0;
    }
    
    ul, ol {
      margin: 8px 0;
      padding-left: 24px;
    }
    
    blockquote {
      border-left: 4px solid #F68D2E;
      border: 1px solid #F68D2E;
      margin: 16px 0;
      padding: 8px 16px;
      background: white;
      font-style: normal;
    }
    
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 16px 0;
      
      th, td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
      }
      
      th {
        background: #f8fafc;
        font-weight: 600;
      }
    }
    
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 8px 0;
    }
    
    a {
      color: #667eea;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    code {
      background: #f1f5f9;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }
    
    pre {
      background: #f1f5f9;
      padding: 12px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 16px 0;
      
      code {
        background: none;
        padding: 0;
      }
    }
  }
  
  .jodit-status-bar {
    background: #f8fafc;
    border-top: 1px solid #e5e7eb;
    padding: 4px 8px;
    font-size: 12px;
    color: #6b7280;
  }
`;

export default CustomJoditEditor;